import { LoaderOverlay } from 'cng-common/components/loaderOverlay'
import { Translation } from 'cng-common/features/translations/components/translation'
import { useSafetyTranslation } from 'cng-common/features/translations/hooks'
import { Button } from 'core-components/library/button'
import { ModalDialog } from 'core-components/library/modalDialog'
import type { IModalContent } from 'core-components/library/modalDialog/models/IModalContent'
import { getFormatDate } from 'core/classes/helpers/DateHelper'
import { getTranslatedText } from 'partner360/helpers/getTranslatedText'
import {
  ChangesPlanningSection,
  type IPlanChangeLog,
} from 'partner360/models/planning/IPlanChangeLog'

import type { AcknowledgmentOffering } from '../model/types'
import { useAcknowledgmentPlanChangesLog } from '../model/useAcknowledgmentPlanChangesLog'
import { ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS } from '../translations'

import styles from './AcknowledgmentPlanChangesDialog.module.scss'

type AcknowledgmentPlanChangesDialogProps = {
  show: boolean
  partnerId: string
  fYear: number
  programType: AcknowledgmentOffering
  changeDate?: string
  isDistributor: boolean
  onClose: () => void
}

type AcknowledgmentPlanChangesDialogContentProps = IModalContent &
  AcknowledgmentPlanChangesDialogProps

type PeriodField = keyof Pick<
  IPlanChangeLog,
  'PlanCYQ1' | 'PlanCYQ2' | 'PlanCYQ3' | 'PlanCYQ4' | 'PlanCY' | 'PlanNY' | 'PlanN2Y'
>

const PERIOD_FIELDS: PeriodField[] = [
  'PlanCYQ1',
  'PlanCYQ2',
  'PlanCYQ3',
  'PlanCYQ4',
  'PlanCY',
  'PlanNY',
  'PlanN2Y',
]

const TRANSLATION_KEYS = {
  title: 'jbp-commitment-changes-log-user-info',
  headFin: 'jbp-commitment-changes-log-head-fin',
  headUnits: 'jbp-commitment-changes-log-head-units',
  headSob: 'jbp-commitment-changes-log-head-sob',
  ok: 'jbp-commitment-changes-log-ok',
  category: 'jbp-commitment-changes-log-category',
  placeholder: 'jbp-commitment-changes-log-placeholder',
}

const TABLE_SECTIONS: Array<{
  section: ChangesPlanningSection
  titleKey: string
}> = [
  {
    section: ChangesPlanningSection.FinancialPlan,
    titleKey: TRANSLATION_KEYS.headFin,
  },
  {
    section: ChangesPlanningSection.SOBPlan,
    titleKey: TRANSLATION_KEYS.headSob,
  },
  {
    section: ChangesPlanningSection.UnitsPlan,
    titleKey: TRANSLATION_KEYS.headUnits,
  },
]

const formatPeriod = (
  period: PeriodField,
  currentYear: number,
  getTranslation: (key: string, replaceParams?: Array<{ key: string; value: string }>) => string
) => {
  const quarterMatch = period.match(/^PlanCYQ(\d)$/)
  if (quarterMatch) {
    return getTranslation(ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.planChanges.periodQuarter, [
      { key: '{quarter}', value: quarterMatch[1] },
    ])
  }

  switch (period) {
    case 'PlanCY':
      return getTranslation(ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.planChanges.periodTotal, [
        { key: '{year}', value: String(currentYear) },
      ])
    case 'PlanNY':
      return getTranslation(ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.planChanges.periodTotal, [
        { key: '{year}', value: String(currentYear + 1) },
      ])
    case 'PlanN2Y':
      return getTranslation(ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.planChanges.periodTotal, [
        { key: '{year}', value: String(currentYear + 2) },
      ])
    default:
      return period
  }
}

const AcknowledgmentPlanChangesDialogContent = ({
  show,
  partnerId,
  fYear,
  programType,
  changeDate,
  isDistributor,
  onClose,
}: AcknowledgmentPlanChangesDialogContentProps) => {
  const t = useSafetyTranslation()
  const { isLoading, groups, selectedGroupIndex, setSelectedGroupIndex, selectedGroup, sections } =
    useAcknowledgmentPlanChangesLog({
      show,
      partnerId,
      fYear,
      programType,
      changeDate,
      isDistributor,
    })

  const translateText = (key: string, replaceParams?: Array<{ key: string; value: string }>) =>
    getTranslatedText({
      t,
      key,
      replaceParams,
    })

  return (
    <div className={styles.root}>
      <LoaderOverlay isVisible={isLoading} color="white" />

      {!isLoading && !selectedGroup ? (
        <div className={styles.placeholder}>
          <Translation text={TRANSLATION_KEYS.placeholder} />
        </div>
      ) : (
        <div className={styles.contentBlock}>
          <div className={styles.info}>
            <Translation
              span
              text={TRANSLATION_KEYS.title}
              htmlAvailable
              replaceParams={[
                { key: '{user}', value: selectedGroup?.modifiedBy ?? '' },
                {
                  key: '{date}',
                  value: selectedGroup?.modified
                    ? getFormatDate(selectedGroup.modified, 'custom', 'DD MMM YYYY') || ''
                    : '',
                },
              ]}
            />
          </div>

          <div className={styles.info}>
            <Translation
              span
              text={TRANSLATION_KEYS.category}
              htmlAvailable
              replaceParams={[
                { key: '{category}', value: selectedGroup?.category ?? '' },
                { key: '{subcategory}', value: selectedGroup?.subCategory ?? '' },
              ]}
            />
          </div>

          {groups.length > 1 ? (
            <div className={styles.pager}>
              <Button
                variant="text"
                iconButton="kbase kbase-omni-chevron-left"
                disabled={selectedGroupIndex <= 0}
                onClick={() => setSelectedGroupIndex((currentIndex) => currentIndex - 1)}
              />
              <span className={styles.pagerCounter}>
                {selectedGroupIndex + 1}/{groups.length}
              </span>
              <Button
                variant="text"
                iconButton="kbase kbase-omni-chevron-right"
                disabled={selectedGroupIndex >= groups.length - 1}
                onClick={() => setSelectedGroupIndex((currentIndex) => currentIndex + 1)}
              />
              <strong className={styles.pagerLabel}>{selectedGroup?.subCategory}</strong>
            </div>
          ) : null}

          <div className={styles.tables}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.emptyHeader}>&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {PERIOD_FIELDS.map((period) => (
                  <tr key={period}>
                    <td className={styles.periodCell}>
                      {formatPeriod(period, fYear, translateText)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {TABLE_SECTIONS.map(({ section, titleKey }) => {
              const sectionData = sections[section]

              if (!sectionData) {
                return null
              }

              return (
                <table key={section} className={styles.table}>
                  <thead>
                    <tr>
                      <th>
                        <Translation text={titleKey} />
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {PERIOD_FIELDS.map((period) => (
                      <tr key={period}>
                        <td>{sectionData[period] || '-'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )
            })}
          </div>
        </div>
      )}

      <div className={styles.footer}>
        <Button onClick={onClose}>
          <Translation text={TRANSLATION_KEYS.ok} span />
        </Button>
      </div>
    </div>
  )
}

export const AcknowledgmentPlanChangesDialog = ({
  show,
  partnerId,
  fYear,
  programType,
  changeDate,
  isDistributor,
  onClose,
}: AcknowledgmentPlanChangesDialogProps) => {
  return (
    <ModalDialog
      closeOnOutsideClick={false}
      show={show}
      size="auto"
      classes={{
        window: styles.window,
        body: styles.body,
      }}
      header={() => (
        <div className={styles.dialogHeader}>
          <div className={styles.title}>
            <Translation
              text={ACKNOWLEDGMENT_STATUS_DIALOG_TRANSLATION_KEYS.planChanges.dialogTitle}
            />
          </div>
        </div>
      )}
      content={AcknowledgmentPlanChangesDialogContent}
      contentProps={{
        show,
        partnerId,
        fYear,
        programType,
        changeDate,
        isDistributor,
        onClose,
      }}
      onClose={onClose}
    />
  )
}
